import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ThreeBackground from './components/ThreeBackground';
import { useAuth } from '../../contexts/AuthContext';
import { postService } from '../../lib/postService';
import Icon from '../../components/AppIcon';
import PostCreator from '../news-feed/components/PostCreator';
import StoriesBar from './components/StoriesBar';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import MediaRender from '../../components/MediaRender';

const AetherFeed = () => {
  const { user: authUser, signOut } = useAuth();
  const navigate = useNavigate();
  const [mcpData, setMcpData] = useState(null);
  useEffect(() => {
    fetch('/mcp_response_latest.json')
      .then((res) => res.json())
      .then(setMcpData)
      .catch((err) => console.error('Failed to load MCP data:', err));
  }, []);

  
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  const currentUser = authUser ? {
    id: authUser.id,
    name: authUser.user_metadata?.full_name || authUser.email,
    username: authUser.user_metadata?.username || authUser.email.split('@')[0],
    avatar: authUser.user_metadata?.avatar_url || "https://images.unsplash.com/photo-1611695434398-4f4b330623e6",
    avatarAlt: "User avatar"
  } : null;

  const mockPosts = [
    {
      id: 1,
      author: {
        name: "Dr. Elena Rostova",
        username: "erostova_ai",
        avatar: "https://images.unsplash.com/photo-1580894732444-8ecded7900cd?w=400&h=400&fit=crop",
        isElite: true,
      },
      content: `AI-Generated Orbital Architecture. Examining the brutalist efficiency of generative AI space station designs and their impact on physical zero-g habitats. #AvailableForContract`,
      images: [{ url: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2000" }],
      timestamp: new Date(Date.now() - 3600000),
      likes: 247,
      comments: []
    },
    {
      id: 2,
      author: {
        name: "Marcus Chen",
        username: "marcus_orbital",
        avatar: "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=400&h=400&fit=crop",
        isElite: true,
      },
      content: `War Space Technology History: The transition from atmospheric deterrence to algorithmic orbital dominance in the late 21st century.`,
      isQuote: true,
      quote: "The next great leap in architecture won't be built on Earth, but engineered by AI in the silence of the void.",
      timestamp: new Date(Date.now() - 7200000),
      likes: 892,
      comments: []
    }
  ];

  const loadInitialPosts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await postService.getPosts();
      if (data && data.length > 0) {
        setPosts(data);
      } else {
        // Generate a very long list of posts initially
        const veryLongPosts = Array.from({ length: 20 }).flatMap((_, i) => 
          mockPosts.map(p => ({ ...p, id: p.id + i * 100, timestamp: new Date(Date.now() - i * 3600000) }))
        );
        setPosts(veryLongPosts);
      }
    } catch (error) {
      console.error("Error loading posts:", error);
      const veryLongPosts = Array.from({ length: 20 }).flatMap((_, i) => 
        mockPosts.map(p => ({ ...p, id: p.id + i * 100, timestamp: new Date(Date.now() - i * 3600000) }))
      );
      setPosts(veryLongPosts);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialPosts();
  }, [loadInitialPosts]);

  useEffect(() => {
    const handleLocalNewPost = () => {
      loadInitialPosts();
    };
    window.addEventListener('new-post-created', handleLocalNewPost);
    return () => window.removeEventListener('new-post-created', handleLocalNewPost);
  }, [loadInitialPosts]);

  const handleScroll = useCallback(() => {
    if (
      window.innerHeight + document.documentElement?.scrollTop >=
      document.documentElement?.offsetHeight - 1000 &&
      !loadingMore &&
      hasMore
    ) {
      loadMorePosts();
    }
  }, [loadingMore, hasMore]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  let updateStatusMut = async () => {};
  let updatePresenceMut = async () => {};
  try {
    updateStatusMut = useMutation(api.profiles.updateStatus);
    updatePresenceMut = useMutation(api.profiles.updatePresence);
  } catch (e) {
    // offline fallback
  }

  // Update presence on mount
  useEffect(() => {
    if (currentUser) {
      updatePresenceMut({ userId: currentUser.id, isOnline: true }).catch(()=>console.log("offline"));
      
      const handleUnload = () => {
        updatePresenceMut({ userId: currentUser.id, isOnline: false }).catch(()=>console.log("offline"));
      };
      window.addEventListener('beforeunload', handleUnload);
      return () => {
        window.removeEventListener('beforeunload', handleUnload);
        handleUnload();
      };
    }
  }, [currentUser]);

  const [statusInput, setStatusInput] = useState('');
  const [editingStatus, setEditingStatus] = useState(false);

  const handleStatusUpdate = async () => {
    if (currentUser) {
      try {
        await updateStatusMut({ userId: currentUser.id, status: statusInput });
        setEditingStatus(false);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const loadMorePosts = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));

    // Generate many additional posts to keep the wall very long
    const additionalPosts = Array.from({ length: 10 }).flatMap((_, i) => 
      mockPosts.map(post => ({
        ...post,
        id: post.id + (page * 1000) + (i * 10),
        timestamp: new Date(Date.now() - (18000000 + (page * i) * 3600000))
      }))
    );

    setPosts((prev) => [...prev, ...additionalPosts]);
    setPage((prev) => prev + 1);
    setLoadingMore(false);

    if (page >= 100) {
      setHasMore(false);
    }
  };

  const themes = [
    { name: 'deep-black', color: '#131313', text: 'white' },
    { name: 'slate-grey', color: '#1e293b', text: 'white' },
    { name: 'crisp-white', color: '#f8fafc', text: 'black' },
    { name: 'old-green', color: '#141e14', text: 'white' }
  ];
  const [currentThemeIndex, setCurrentThemeIndex] = useState(0);

  const toggleTheme = () => {
    const nextIndex = (currentThemeIndex + 1) % themes.length;
    setCurrentThemeIndex(nextIndex);
    const theme = themes[nextIndex];
    document.body.className = `theme-${theme.name} bg-background text-on-background font-body-md selection:bg-primary/30`;
    document.documentElement.style.setProperty('--bg-overlay', theme.name === 'crisp-white' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.5)');
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <>
      <ThreeBackground />

      <header className="fixed top-0 w-full z-50 bg-background/40 backdrop-blur-[40px] border-b border-primary/10 h-20">
        <div className="flex justify-between items-center w-full px-margin-desktop max-w-container-max mx-auto h-full">
          <div className="font-headline-lg text-headline-lg tracking-widest text-primary">AETHER</div>
          <nav className="hidden md:flex items-center space-x-12">
            <a className="font-label-caps text-label-caps text-on-surface-variant hover:text-primary transition-colors duration-300 cursor-pointer" onClick={() => navigate('/curation')}>Curation</a>
            <a className="font-label-caps text-label-caps text-on-surface-variant hover:text-primary transition-colors duration-300" href="#">Archives</a>
            <a className="font-label-caps text-label-caps text-on-surface-variant hover:text-primary transition-colors duration-300 cursor-pointer" onClick={() => navigate('/salon')}>Salon</a>
          </nav>
          <div className="flex items-center space-x-6">
            <span className="material-symbols-outlined text-primary cursor-pointer hover:opacity-80 transition-opacity" onClick={toggleTheme}>contrast</span>
            <span className="material-symbols-outlined text-primary cursor-pointer hover:opacity-80 transition-opacity" onClick={() => navigate('/curation')}>account_circle</span>
          </div>
        </div>
      </header>

      <aside className="fixed left-0 top-0 h-screen w-64 z-40 bg-surface-container-low/30 backdrop-blur-[24px] border-r border-white/5 flex flex-col p-stack-sm pt-24 hidden md:flex">
        {currentUser && (
          <div className="flex items-center space-x-4 mb-10 px-4">
            <div className="w-10 h-10 rounded-full bg-surface-variant overflow-hidden rose-gold-frame p-[1px] relative">
              <img alt="Curator Profile" className="w-full h-full object-cover rounded-full" src={currentUser.avatar} />
              <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-background"></div>
            </div>
            <div>
              <div className="font-body-md text-primary font-bold">{currentUser.name}</div>
              
              {editingStatus ? (
                <div className="mt-1 flex gap-1">
                  <input 
                    autoFocus
                    type="text" 
                    placeholder="Status?"
                    value={statusInput}
                    onChange={(e) => setStatusInput(e.target.value)}
                    className="w-full bg-background/50 border border-white/10 rounded px-1 text-[10px] text-primary outline-none"
                  />
                  <button onClick={handleStatusUpdate} className="text-primary hover:text-green-400">
                    <span className="material-symbols-outlined text-[14px]">check</span>
                  </button>
                </div>
              ) : (
                <div 
                  onClick={() => setEditingStatus(true)}
                  className="text-[10px] uppercase tracking-widest text-on-surface-variant/60 mt-1 cursor-pointer hover:text-primary transition-colors flex items-center gap-1"
                >
                  <span>{statusInput || "Elite Member"}</span>
                  <span className="material-symbols-outlined text-[10px]">edit</span>
                </div>
              )}
            </div>
          </div>
        )}

        <nav className="flex-1 flex flex-col space-y-2">
          <a className="flex items-center space-x-4 px-4 py-3 text-primary font-bold bg-white/5 rounded-full transition-all duration-300" href="#">
            <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>auto_awesome</span>
            <span className="font-body-md">Feed</span>
          </a>
          <a className="flex items-center space-x-4 px-4 py-3 text-on-surface-variant hover:bg-white/10 rounded-full transition-all duration-300" href="#">
            <span className="material-symbols-outlined">explore</span>
            <span className="font-body-md">Discovery</span>
          </a>
          <a className="flex items-center space-x-4 px-4 py-3 text-on-surface-variant hover:bg-white/10 rounded-full transition-all duration-300 cursor-pointer" onClick={() => navigate('/gaming')}>
            <span className="material-symbols-outlined text-[#00ffcc]">sports_esports</span>
            <span className="font-body-md text-[#00ffcc]">Game News</span>
          </a>
          <a className="flex items-center space-x-4 px-4 py-3 text-on-surface-variant hover:bg-white/10 rounded-full transition-all duration-300 cursor-pointer" onClick={() => navigate('/reels')}>
            <span className="material-symbols-outlined">play_circle</span>
            <span className="font-body-md">Reels</span>
          </a>
          <a className="flex items-center space-x-4 px-4 py-3 text-on-surface-variant hover:bg-white/10 rounded-full transition-all duration-300 cursor-pointer" onClick={() => navigate('/architecture')}>
            <span className="material-symbols-outlined">domain</span>
            <span className="font-body-md">Architecture</span>
          </a>
          <a className="flex items-center space-x-4 px-4 py-3 text-aether-gold hover:bg-white/10 rounded-full transition-all duration-300 border border-[#d4af37]/20 cursor-pointer" onClick={() => navigate('/contracts')}>
            <span className="material-symbols-outlined text-[#d4af37]">work</span>
            <span className="font-body-md text-[#d4af37]">Contracts</span>
          </a>
          <a className="flex items-center space-x-4 px-4 py-3 text-on-surface-variant hover:bg-white/10 rounded-full transition-all duration-300" href="#">
            <span className="material-symbols-outlined">chat_bubble_outline</span>
            <span className="font-body-md">Messages</span>
          </a>
          <a className="flex items-center space-x-4 px-4 py-3 text-on-surface-variant hover:bg-white/10 rounded-full transition-all duration-300" href="#">
            <span className="material-symbols-outlined">auto_stories</span>
            <span className="font-body-md">Library</span>
          </a>
        </nav>

        <button className="mt-8 mb-8 mx-4 py-3 border border-white/20 rounded-full font-label-caps text-label-caps hover:bg-white/5 transition-colors uppercase tracking-[0.2em] text-white">
          Post Entry
        </button>

        <div className="flex flex-col space-y-2 pt-8 border-t border-white/5">
          <a className="flex items-center space-x-4 px-4 py-2 text-on-surface-variant hover:text-primary transition-colors" href="#">
            <span className="material-symbols-outlined text-[16px]">settings</span>
            <span className="text-xs font-label-caps uppercase text-white/60">Settings</span>
          </a>
          <a className="flex items-center space-x-4 px-4 py-2 text-on-surface-variant hover:text-primary transition-colors" href="#">
            <span className="material-symbols-outlined text-[16px]">help_outline</span>
            <span className="text-xs font-label-caps uppercase text-white/60">Support</span>
          </a>
        </div>
      </aside>

      <main className="md:ml-64 pt-32 px-margin-mobile md:px-margin-desktop pb-32 w-full max-w-container-max mx-auto relative z-10 min-h-screen">
        <div className="w-full space-y-stack-lg">
          
          <div className="max-w-3xl mx-auto mb-12">
            <h2 className="font-headline-xl text-headline-xl mb-4 italic opacity-90 text-white">Stories</h2>
            <div className="mb-8">
              <StoriesBar currentUser={currentUser} />
            </div>

            <h2 className="font-headline-xl text-headline-xl mb-4 italic opacity-90 text-white">Today's Moments</h2>
            
            {currentUser && (
              <PostCreator user={currentUser} />
            )}
          </div>

          <div className="w-full">
            {loading ? (
              <div className="flex justify-center items-center py-20 text-primary">
                <Icon name="Loader" size={32} className="animate-spin" />
              </div>
            ) : (
              <div className="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-6">
                {posts.map((post) => {
                  const hasImage = post.images?.[0] || post.image_url;
                const hasVideo = post.video_url || post.media_type === 'video';
                const isQuote = post.isQuote || (!hasImage && !hasVideo && post.content?.length < 150);

                if (isQuote) {
                  return (
                    <article key={post.id} className="break-inside-avoid mb-6 glass-panel rounded-xl p-8 space-y-6">
                      <div className="flex items-center space-x-4">
                        <div className="w-8 h-8 rounded-full bg-surface-variant overflow-hidden">
                          <img alt="User" className="w-full h-full object-cover" src={post.author?.avatar || "https://images.unsplash.com/photo-1611695434398-4f4b330623e6"} />
                        </div>
                        <div>
                          <h3 className="font-body-md font-bold text-primary flex items-center gap-1">
                            {post.author?.name || 'Curator'}
                            {post.author?.isElite && (
                              <span className="material-symbols-outlined text-[14px]" style={{ color: '#d4af37', fontVariationSettings: "'FILL' 1" }}>verified</span>
                            )}
                          </h3>
                          <p className="text-[10px] uppercase tracking-widest text-on-surface-variant/60">Digital Salon • {new Date(post.timestamp || post.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
                        </div>
                      </div>
                      <blockquote className="border-l-2 border-primary/20 pl-8">
                        <p className="font-headline-lg text-headline-lg text-primary italic leading-tight">
                          "{post.quote || post.content}"
                        </p>
                      </blockquote>
                      {post.quote && (
                        <p className="font-body-md text-on-surface-variant leading-relaxed">
                          {post.content}
                        </p>
                      )}
                      <div className="flex items-center space-x-8 pt-4 border-t border-white/5">
                        <button className="flex items-center space-x-2 text-on-surface-variant hover:text-primary transition-colors">
                          <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 0" }}>favorite</span>
                          <span className="text-xs font-label-caps">{post.likes || 0} Appreciations</span>
                        </button>
                        <button className="flex items-center space-x-2 text-on-surface-variant hover:text-primary transition-colors">
                          <span className="material-symbols-outlined">share</span>
                          <span className="text-xs font-label-caps">Share</span>
                        </button>
                      </div>
                    </article>
                  );
                }

                // Default Card (Card 1 with Image or Video)
                return (
                  <article key={post.id} className="break-inside-avoid mb-6 glass-panel rounded-xl overflow-hidden group">
                    {(hasImage || hasVideo) && (
                      <div className="relative aspect-[16/10] bg-black/40">
                        {hasVideo ? (
                          <MediaRender 
                            type="video"
                            className="w-full h-full object-contain"
                            src={post.video_url || post.image_url}
                            controls
                            playsInline
                          />
                        ) : (
                          <>
                            <MediaRender type="image" alt="Featured Media" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" src={post.images?.[0]?.url || post.image_url} />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          </>
                        )}
                      </div>
                    )}
                    <div className="p-8 space-y-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-8 h-8 rounded-full bg-surface-variant overflow-hidden">
                            <img alt="User" className="w-full h-full object-cover" src={post.author?.avatar || "https://images.unsplash.com/photo-1611695434398-4f4b330623e6"} />
                          </div>
                          <div>
                            <h3 className="font-body-md font-bold text-primary flex items-center gap-1">
                              {post.author?.name || 'Curator'}
                              {post.author?.isElite && (
                                <span className="material-symbols-outlined text-[14px]" style={{ color: '#d4af37', fontVariationSettings: "'FILL' 1" }}>verified</span>
                              )}
                            </h3>
                            <p className="text-[10px] uppercase tracking-widest text-on-surface-variant/60">{new Date(post.timestamp || post.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
                          </div>
                        </div>
                        <span className="material-symbols-outlined text-on-surface-variant cursor-pointer">more_horiz</span>
                      </div>
                      <p className="font-body-lg text-body-lg leading-relaxed text-on-surface text-white/90">
                        {post.content}
                      </p>
                      <div className="flex items-center space-x-8 pt-4 border-t border-white/5">
                        <button className="flex items-center space-x-2 text-on-surface-variant hover:text-primary transition-colors">
                          <span className="material-symbols-outlined text-white" style={{ fontVariationSettings: "'FILL' 0" }}>favorite</span>
                          <span className="text-xs font-label-caps text-white">{post.likes || 0} Appreciations</span>
                        </button>
                        <button className="flex items-center space-x-2 text-on-surface-variant hover:text-primary transition-colors">
                          <span className="material-symbols-outlined text-white" style={{ fontVariationSettings: "'FILL' 0" }}>share</span>
                          <span className="text-xs font-label-caps text-white">Share</span>
                        </button>
                        <button className="flex items-center space-x-2 text-on-surface-variant hover:text-primary transition-colors ml-auto">
                          <span className="material-symbols-outlined text-white">bookmark</span>
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
              </div>
            )}

            {loadingMore && (
              <div className="flex justify-center items-center py-6 text-primary">
                <Icon name="Loader" size={24} className="animate-spin" />
              </div>
            )}
          </div>
        </div>
      </main>

    </>
  );
};

export default AetherFeed;
